import { Badge, Box, Button, Flex, Group, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconClockOff,
  IconFilePencil,
  IconFilePlus,
  IconFileX,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ProposedAction } from '../chat/types';

type Props = {
  proposal: ProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

const OPERATION_ICONS = {
  CREATE: IconFilePlus,
  UPDATE: IconFilePencil,
  DELETE: IconFileX,
} as const;

const OPERATION_NOUNS = {
  CREATE: 'creation',
  UPDATE: 'update',
  DELETE: 'deletion',
} as const;

const TYPE_LABELS = {
  MEETING_NOTE: 'Meeting note template',
  MEETING_AGENDA: 'Meeting agenda template',
} as const;

type StatBadge = { label: string; color: string };

function changeStatBadges(proposal: ProposedAction): StatBadge[] {
  const stats = proposal.changeStats;
  if (!stats) return [];
  const badges: StatBadge[] = [];
  if (stats.sectionsAdded > 0) {
    badges.push({
      label: `${stats.sectionsAdded} section${stats.sectionsAdded === 1 ? '' : 's'} added`,
      color: 'success',
    });
  }
  if (stats.sectionsModified > 0) {
    badges.push({
      label: `${stats.sectionsModified} section${stats.sectionsModified === 1 ? '' : 's'} modified`,
      color: 'orange',
    });
  }
  if (stats.sectionsRemoved > 0) {
    badges.push({
      label: `${stats.sectionsRemoved} section${stats.sectionsRemoved === 1 ? '' : 's'} removed`,
      color: 'error',
    });
  }
  return badges;
}

function DecidedLine({ proposal }: { proposal: ProposedAction }) {
  const iconProps = { size: 16, stroke: 1.5, 'aria-hidden': true } as const;
  switch (proposal.status) {
    case 'APPLIED':
      return (
        <Group gap="xxs" align="center">
          <IconCircleCheck {...iconProps} color="var(--mantine-color-success-7)" />
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="success.8">
            Approved and applied
          </Text>
        </Group>
      );
    case 'DENIED':
      return (
        <Group gap="xxs" align="center">
          <IconCircleX {...iconProps} color="var(--mantine-color-gray-6)" />
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.7">
            Denied
          </Text>
        </Group>
      );
    case 'FAILED':
      return (
        <Group gap="xxs" align="flex-start" wrap="nowrap">
          <IconAlertTriangle {...iconProps} color="var(--mantine-color-warning-7)" />
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="warning.8">
            {proposal.failureReason
              ? `Approved but not applied: ${proposal.failureReason}`
              : 'Approved but not applied'}
          </Text>
        </Group>
      );
    case 'SUPERSEDED':
      return (
        <Group gap="xxs" align="center">
          <IconClockOff {...iconProps} color="var(--mantine-color-gray-6)" />
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.7">
            This proposal has expired
          </Text>
        </Group>
      );
    default:
      return null;
  }
}

export function TemplateProposalWidget({ proposal, onDecide, disabled }: Props) {
  const OperationIcon = OPERATION_ICONS[proposal.operation];
  const badges = changeStatBadges(proposal);
  const isPending = proposal.status === 'PENDING' || !proposal.status;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <OperationIcon size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Box>
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
              {proposal.templateName}
            </Text>
            <Text size="textXs" c="gray.7">
              {TYPE_LABELS[proposal.templateType]} · proposed {OPERATION_NOUNS[proposal.operation]}
            </Text>
          </Box>
        </Group>
        <Text size="textSm" c="gray.8">
          {proposal.summary}
        </Text>
        {badges.length > 0 && (
          <Group gap="xxs">
            {badges.map(badge => (
              <Badge key={badge.label} variant="light" color={badge.color} size="sm" tt="none">
                {badge.label}
              </Badge>
            ))}
          </Group>
        )}
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <DecidedLine proposal={proposal} />
        )}
      </Flex>
    </Box>
  );
}
